<template>
    <div class="copyable" :class="{ 'copyable--block': block }">
        <pre v-if="block" class="copyable-value">{{ value }}</pre>
        <code v-else class="copyable-value">{{ value }}</code>
        <button type="button" class="copyable-button" :aria-label="label" @click="copy">
            <PhCheck v-if="copied" :size="16" aria-hidden="true" />
            <PhCopy v-else :size="16" aria-hidden="true" />
        </button>
        <!-- The tick is the confirmation for anyone who can see it; this is the same confirmation
             for anyone who cannot, said once. The button keeps its name, so a screen reader is
             not told twice. The region is on the page before the copy, so the change is read out. -->
        <span class="sr-only" role="status">{{ announcement }}</span>
    </div>
</template>

<script setup lang="ts">
/**
 * A value the member pastes somewhere else (an address, a command, a config file), shown as it
 * will be pasted, with a button that copies it exactly.
 *
 * The button copies the `value` prop, never the rendered text, so what lands on the clipboard is
 * byte for byte what the caller built, line breaks included. A write the browser refuses is said
 * in a toast and the tick never shows, because a copy that did not happen must not look like one.
 */
import { PhCheck, PhCopy } from '@phosphor-icons/vue';
import { onBeforeUnmount, ref } from 'vue';
import { useGlobalToast } from '~/composables/useGlobalToast';
import { useLiveAnnouncer } from '~/composables/useLiveAnnouncer';

const props = defineProps<{
    value: string;
    /** The button's name, naming what it copies: "Copy the server URL". */
    label: string;
    /** A multi-line value, kept as written rather than wrapped into one line. */
    block?: boolean;
}>();

/** How long the tick stands before the button offers to copy again. */
const CONFIRMATION_MS = 2000;

const { showError } = useGlobalToast();
const { announcement, announce } = useLiveAnnouncer();

const copied = ref(false);
let reset: ReturnType<typeof setTimeout> | undefined;

async function copy() {
    try {
        await navigator.clipboard.writeText(props.value);
    } catch {
        showError('Copy failed', 'Select the text and copy it yourself.');
        return;
    }

    copied.value = true;
    void announce('Copied');
    clearTimeout(reset);
    reset = setTimeout(() => {
        copied.value = false;
    }, CONFIRMATION_MS);
}

onBeforeUnmount(() => clearTimeout(reset));
</script>

<style lang="scss" scoped>
.copyable {
    @apply flex items-center gap-1 min-w-0;
    padding: var(--space-1) var(--space-1) var(--space-1) var(--space-3);
    border: 1px solid var(--hairline);
    border-radius: var(--radius-control);
    background: var(--surface-sunken);
}

.copyable--block {
    @apply items-start;
}

// The value wraps at any character rather than scrolling: a URL or a command has no spaces to
// break at, and a box that scrolls sideways hides the part the member has to check.
.copyable-value {
    @apply flex-1 min-w-0 m-0;
    color: var(--ink);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    line-height: var(--leading-xs);
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.copyable--block .copyable-value {
    padding-top: var(--space-2);
}

// Icon-only, square at the small control height: the design system's tertiary button.
.copyable-button {
    @apply flex items-center justify-center flex-shrink-0 border-0 bg-transparent cursor-pointer;
    width: var(--control-h-sm);
    height: var(--control-h-sm);
    border-radius: var(--radius-control);
    color: var(--ink-body);
    transition: background-color var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard);

    &:hover {
        background: var(--surface);
        color: var(--ink);
    }
}
</style>
